import { useAuth } from '@clerk/nextjs';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { Conversation, MessageReponseDto, MessageRequestDto } from '@/types/message';
import type { SessionMessage } from '@/types/Api';
import { getApiBaseUrl } from '@/lib/apiUrl';

export const useMessageService = () => {
  const { getToken } = useAuth();

  const sendMessage = async (messageDto: MessageRequestDto): Promise<SessionMessage> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<SessionMessage> = await axios.post<SessionMessage>(
        `${getApiBaseUrl()}/bookings/${messageDto.bookingId}/messages`,
        messageDto,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw new Error('Failed to send message');
    }
  };

  const getMessages = async (bookingId: string): Promise<MessageReponseDto[]> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<MessageReponseDto[]> = await axios.get<MessageReponseDto[]>(
        `${getApiBaseUrl()}/bookings/${bookingId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw new Error('Failed to fetch messages');
    }
  };

  const getUserConversations = async (userId: string): Promise<Conversation[] | null> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<Conversation[]> = await axios.get<Conversation[]>(
        `${getApiBaseUrl()}/bookings/${userId}/conversations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (_error) {
      console.error('Error getting message');
      return null;
    }
  };

  const getExpertConversations = async (
    expertId: string,
    limit?: number,
  ): Promise<Conversation[]> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<Conversation[]> = await axios.get<Conversation[]>(
        `${getApiBaseUrl()}/bookings/${expertId}/expert-conversations${limit ? `?limit=${limit}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching expert conversations:', error);
      throw new Error('Failed to fetch expert conversations');
    }
  };

  const getTotalUnreadMessagesCount = async (authUserId: string): Promise<{ count: number }> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<{ count: number }> = await axios.get<{ count: number }>(
        `${getApiBaseUrl()}/bookings/${authUserId}/unread-messages-count`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching unread messages count:', error);
      throw new Error('Failed to fetch unread messages count');
    }
  };

  const getTotalUnreadMessagesCountForExpert = async (
    expertExternalId: string,
  ): Promise<{ count: number }> => {
    const token = await getToken();
    try {
      const response: AxiosResponse<{ count: number }> = await axios.get<{ count: number }>(
        `${getApiBaseUrl()}/bookings/${expertExternalId}/expert-unread-messages-count`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching expert unread messages count:', error);
      throw new Error('Failed to fetch expert unread messages count');
    }
  };

  return {
    sendMessage,
    getMessages,
    getUserConversations,
    getExpertConversations,
    getTotalUnreadMessagesCount,
    getTotalUnreadMessagesCountForExpert,
  };
};
